import React, { useEffect, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Form,
  Input,
  Button,
  Label,
  InputGroup,
  InputGroupAddon,
  Spinner,
} from 'reactstrap';
import { connect, useSelector } from 'react-redux';
import { Link, withRouter, Redirect } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';

//i18n
import { useTranslation } from 'react-i18next';

//redux store
import { loginUser, apiError } from '../../redux/actions';

//Import Images
import { getBrowser, getLocalStorage } from 'src/helpers';
import { KEY_IP_ADDRESS_STORAGE } from 'src/redux/auth/constants';
import config from 'src/config';
import messengerIcon from 'src/assets/images/Icons/messenger.svg';

import {
  LABEL_TRACKING,
  ACTION_TRACKING,
  CATEGORY_TRACKING,
} from 'src/constants';
import googleAnalytic from 'src/helpers/googleAnalytic';
import SlideImage from './components/SlideImage';

/**
 * Login component
 * @param {*} props
 */
const Login = (props) => {
  /* intilize t variable for multi language implementation */
  const { t } = useTranslation();
  const ipLocal = getLocalStorage(KEY_IP_ADDRESS_STORAGE);

  const loading = useSelector((state) => state.Auth.loading);
  const error = useSelector((state) => state.Auth.error);
  const browserInfo = getBrowser();
  const browserName = browserInfo?.name;

  const [showPassword, setShowPassword] = useState(false);

  const clearError = () => {
    props.apiError('');
  };

  useEffect(() => {
    googleAnalytic(
      CATEGORY_TRACKING.USER,
      ACTION_TRACKING.OPEN,
      LABEL_TRACKING.LOGIN
    );
    return () => {
      clearError();
    };
    // eslint-disable-next-line
  }, []);

  // validation
  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: Yup.object({
      email: Yup.string().required('Please Enter Your Username'),
      password: Yup.string().required('Please Enter Your Password'),
    }),
    onSubmit: (values) => {
      if (!loading) {
        const device_id = `${ipLocal} ${browserName}`;
        props.loginUser(
          values.email,
          values.password,
          props.history,
          device_id
        );
      }
    },
  });

  if (localStorage.getItem(config.STORAGE_KEY_USER)) {
    return <Redirect to="/" />;
  }

  const handleShowHidePassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <React.Fragment>
      <div className="account-pages">
        <Container fluid>
          <Row>
            <Col md={6} sm={12} className="p-0">
              <div className="left-background">
                <SlideImage />
              </div>
            </Col>
            <Col md={6} sm={12} className="p-0">
              <Row
                className="justify-content-center ml-0 mr-0"
                style={{ height: '100%' }}
              >
                <Col md={12} className="pl-0">
                  <Card style={{ height: '100%' }}>
                    <CardBody className="p-5" style={{ position: 'relative' }}>
                      <div className="p-3">
                        <h2 className="mb-5">
                          {t('Login').toLocaleUpperCase()}
                        </h2>
                        <Form onSubmit={formik.handleSubmit}>
                          <FormGroup>
                            <Label className="subtitle1">{t('Email')}</Label>
                            <InputGroup className="mb-2 input-group-lg input-group-custom">
                              <InputGroupAddon addonType="prepend">
                                <span className="input-group-text border-light text-dark addon-left-custom">
                                  <i className="ri-mail-fill" color="black"></i>
                                </span>
                              </InputGroupAddon>
                              <Input
                                type="text"
                                id="email"
                                name="email"
                                className="form-control border-light input-custom email-input-custom pl-0"
                                placeholder={t('form_register.enter_email')}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.email}
                                invalid={
                                  formik.touched.email && formik.errors.email
                                    ? true
                                    : false
                                }
                              />
                            </InputGroup>
                            {error && <p className="error-message">{error}</p>}
                          </FormGroup>

                          <FormGroup className="mb-4">
                            <Label className="subtitle1">{t('Password')}</Label>
                            <InputGroup className="mb-3 input-group-lg input-group-custom">
                              <InputGroupAddon addonType="prepend">
                                <span className="input-group-text border-light text-dark addon-left-custom">
                                  <i className="ri-lock-2-fill"></i>
                                </span>
                              </InputGroupAddon>
                              <Input
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                name="password"
                                className="form-control border-light input-custom password-input-custom pl-0"
                                placeholder={t('form_register.enter_password')}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.password}
                                invalid={
                                  formik.touched.password &&
                                  formik.errors.password
                                    ? true
                                    : false
                                }
                              />
                              <InputGroupAddon addonType="append">
                                <span
                                  onClick={handleShowHidePassword}
                                  className="input-group-text border-lighttext-dark addon-right-custom"
                                >
                                  <i
                                    className={
                                      showPassword
                                        ? 'ri-eye-fill'
                                        : 'ri-eye-off-fill'
                                    }
                                  ></i>
                                </span>
                              </InputGroupAddon>
                            </InputGroup>
                          </FormGroup>
                          <div className="forgot-password-btn subtitle2">
                            <Link to="/forget-password">
                              {t('forgot_password')}
                            </Link>
                          </div>
                          <Button
                            color="primary"
                            block
                            className="mb-2 action-form-button subtitle1"
                            type="submit"
                          >
                            {loading ? <Spinner size="sm" /> : t('Login')}
                          </Button>
                        </Form>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <p className="subtitle2">
                          {t('no_account')}
                          <Link to="/register" className="ml-1">
                            {t('Register')}
                          </Link>
                        </p>
                        <p
                          className="subtitle2 d-flex align-items-center"
                          style={{ position: 'absolute', bottom: '32px' }}
                        >
                          <div>{t('contact_us')} </div>
                          <a
                            href="https://www.messenger.com/t/pombeebeestudybuddy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className=""
                            style={{
                              background: '#f9c901',
                              padding: '5px 10px',
                              borderRadius: '16px',
                              marginLeft: '10px',
                            }}
                          >
                            <img
                              src={messengerIcon}
                              alt=""
                              style={{ width: '20px', marginRight: '10px' }}
                            />
                            <span className="text-white">Pombeebee</span>
                          </a>
                        </p>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

const mapStateToProps = (state) => {
  const { user, loading, error } = state.Auth;
  return { user, loading, error };
};

export default withRouter(
  connect(mapStateToProps, { loginUser, apiError })(Login)
);
